import React, { useState } from "react";
import {
  Button,
  Divider,
  List,
  Modal,
  Tabs,
  TabsProps,
  Typography,
} from "antd";
import {
  BankOutlined,
  FileDoneOutlined,
  LaptopOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import ResumeSelector from "./ResumeSelector";
import ResumePreview from "./ResumePreview";
import ProfileAnalysisView from "./ProfileAnalysisView";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import { useAILoader } from "../../Context/AILoaderContext/AILoaderContext";
import { JobDescription } from "../../Entities/job-description.entity";
import { openNotification } from "../Notification/Index";
import { Userprofile } from "../../Entities/user-profile.entity";
import { ParsedResumeContent } from "../../Interface/Index";
import "./index.scss";

interface ResumeUploaderProps {
  isOpen: boolean;
  handleClose: () => void;
  handleFileUploadAndAnalyze?: (file: File, parsedResumeData: ParsedResumeContent) => void | Promise<void>;
  handleContinueWithProfile?: (profile: Userprofile) => Promise<void>;
  jobDescription?: JobDescription;
  isDirectUpload?: boolean;
  onSave?: (file: File, resumeData: ParsedResumeContent, plainText: string) => Promise<void>;
  onSaveSuccess?: (userProfile: any) => void;
}

const ResumeUploader: React.FC<ResumeUploaderProps> = ({
  handleClose,
  isOpen,
  handleFileUploadAndAnalyze,
  handleContinueWithProfile,
  jobDescription,
  isDirectUpload = false,
  onSave,
  onSaveSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<string>("1");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<Userprofile | null>(
    null
  );
  const [isInfoDismissed, setIsInfoDismissed] = useState<boolean>(false);
  const [editedResumeData, setEditedResumeData] = useState<ParsedResumeContent | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [analysisMessage, setAnalysisMessage] = useState<string>("");
  const [parsedResumeData, setParsedResumeData] = useState<ParsedResumeContent | null>(null);

  const profileMatcherService = new ProfileMatcherService();
  const { setIsShowAILoader, setAiLoaderText, clearAILoader } = useAILoader();

  const handleCloseModal = () => {
    setActiveTab("1");
    setResumeFile(null);
    setSelectedProfile(null);
    setIsInfoDismissed(false);
    setEditedResumeData(null);
    setParsedResumeData(null);
    setIsAnalyzing(false);
    setAnalysisMessage("");
    clearAILoader();
    handleClose();
  };

  const onTabChange = (key: string) => {
    setActiveTab(key);
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Select Resume",
    },
  ];

  if (activeTab === "2" || activeTab === "3") {
    items.push({
      key: "2",
      label: "Review Resume",
    });
  }

  // Only show analysis tab if not in direct upload mode and job description exists
  if (activeTab === "3" && !isDirectUpload && jobDescription) {
    items.push({
      key: "3",
      label: "Profile Analysis",
    });
  }

  const data = [
    {
      title: "Educational qualification",
      icon: <FileDoneOutlined />,
    },
    {
      title: "Work experience",
      icon: <BankOutlined />,
    },
    {
      title: "Technology proficiency",
      icon: <LaptopOutlined />,
    },
    // {
    //   title: "Achievements and awards",
    //   icon: <TrophyOutlined />,
    // },
  ];

  const handleResumeFileUpload = async (file: File) => {
    setSelectedProfile(null);
    setResumeFile(file);
    setParsedResumeData(null);
    setIsShowAILoader(true);
    setAiLoaderText("Extracting resume content...");
    try {
      const data = await profileMatcherService.getFormattedResumeByResumeRawData(file);
      if (data) {
        setParsedResumeData(data);
        openNotification(
          "success",
          "Resume Extracted",
          "Resume content has been extracted successfully."
        );
        setActiveTab("2");
      } else {
        openNotification(
          "error",
          "Unable to Process Resume",
          "Please try re-uploading or enter your details manually."
        );
      }
    } catch (error) {
      console.error("Error while parsing resume", error);
      openNotification(
        "error",
        "Unable to Process Resume",
        "Please try re-uploading or enter your details manually."
      );
    } finally {
      clearAILoader();
    }
  };

  const handleRemoveResumeFile = () => {
    setResumeFile(null);
    setParsedResumeData(null);
  };

  const handleExistingResumeSelection = (userProfile: Userprofile) => {
    if (selectedProfile && selectedProfile._id === userProfile._id) {
      setSelectedProfile(null);
    } else {
      setSelectedProfile(userProfile);
    }

    if (resumeFile) {
      handleRemoveResumeFile();
    }
  };

  const handleContinueWithResumeFile = async () => {
    if (!resumeFile) {
      return;
    }
    setActiveTab("2");
  };

  const handleContinueWithSelectedProfile = async () => {
    if (isDirectUpload) {
      // In direct upload mode, we can't use existing profile
      openNotification(
        "info",
        "Upload New Resume",
        "Please upload a new resume file for direct upload."
      );
      return;
    }
    if (selectedProfile && handleContinueWithProfile) {
      handleCloseModal();
      await handleContinueWithProfile(selectedProfile);
    }
    return;
  };

  const handleContinue = async () => {
    if (resumeFile && !selectedProfile) {
      await handleContinueWithResumeFile();
      return;
    } else if (!resumeFile && selectedProfile) {
      await handleContinueWithSelectedProfile();
      return;
    }
  };

  const handleGotItClick = () => {
    setIsInfoDismissed(true);
  };

  const handleReuploadFromReview = () => {
    // Go back to tab 1 and clear the file so user can upload a new one
    setResumeFile(null);
    setEditedResumeData(null);
    setParsedResumeData(null);
    setActiveTab("1");
  };

  // Handler to start analysis or save - called from ResumePreview
  const handleStartAnalysis = async (resumeData: ParsedResumeContent, plainText: string) => {
    if (!resumeFile) {
      openNotification(
        "error",
        "No Resume Uploaded",
        "Please upload a resume file to begin."
      );
      return;
    }

    // Store the edited resume data
    setEditedResumeData(resumeData);

    if (isDirectUpload && onSave) {
      // Direct upload mode - save the resume
      try {
        await onSave(resumeFile, resumeData, plainText);
        handleCloseModal();
      } catch (error) {
        console.error("Error saving resume:", error);
        openNotification(
          "error",
          "Save Failed",
          "Failed to save resume. Please try again."
        );
      }
      return;
    }

    // Job application mode - analyze against job requirements
    if (!jobDescription || !handleFileUploadAndAnalyze) {
      openNotification(
        "error",
        "Configuration Error",
        "Job description or analysis handler not available."
      );
      return;
    }

    // Switch to analysis tab
    setActiveTab("3");
    setIsAnalyzing(true);
    setAnalysisMessage("Analyzing your profile against job requirements...");

    try {
      // Call the actual analysis function with parsed resume data
      await handleFileUploadAndAnalyze(resumeFile, resumeData);
    } catch (error) {
      console.error("Error during analysis:", error);
      openNotification(
        "error",
        "Analysis Failed",
        "Something went wrong during profile analysis. Please try again."
      );
    } finally {
      // Close the modal after analysis completes (success or failure)
      // The parent component handles navigation and results display
      handleCloseModal();
    }
  };

  // Check if we should hide the info panel (on analysis tab, but not in direct upload mode)
  const shouldHideInfoPanel = activeTab === "3" && !isDirectUpload;

  const getResumeUploaderTabsContent = () => {
    if (activeTab === "1") {
      return (
        <ResumeSelector
          handleResumeFileUpload={handleResumeFileUpload}
          handleExistingResumeSelection={isDirectUpload ? undefined : handleExistingResumeSelection}
          resumeFile={resumeFile}
          selectedProfile={isDirectUpload ? null : selectedProfile}
          handleRemoveResumeFile={handleRemoveResumeFile}
          handleContinue={handleContinue}
        />
      );
    } else if (activeTab === "2") {
      return (
        <ResumePreview
          handleFileUploadAndAnalyze={
            handleFileUploadAndAnalyze
              ? (file, parsedResumeData) => {
                  const result = handleFileUploadAndAnalyze(file, parsedResumeData);
                  return result instanceof Promise ? result : Promise.resolve();
                }
              : undefined
          }
          resumeFile={resumeFile}
          handleClose={handleCloseModal}
          onReupload={handleReuploadFromReview}
          onStartAnalysis={isDirectUpload ? undefined : handleStartAnalysis}
          onSave={isDirectUpload ? onSave : undefined}
          isDirectUpload={isDirectUpload}
          initialParsedResume={parsedResumeData}
        />
      );
    } else if (activeTab === "3" && !isDirectUpload && jobDescription) {
      return (
        <ProfileAnalysisView
          jobRequirements={jobDescription?.requirements || []}
          resumeData={editedResumeData || null}
          jobTitle={jobDescription?.title}
          isAnalyzing={isAnalyzing}
          analysisMessage={analysisMessage}
        />
      );
    }
    return null;
  };

  return (
    <section className="resume-uploader-section">
      <Modal
        centered
        closeIcon={false}
        open={isOpen}
        onCancel={handleCloseModal}
        footer={null}
        width="100%"
        style={{ maxWidth: "1200px" }}
        className="full-width-modal"
        destroyOnHidden
        maskClosable={!isAnalyzing}
      >
        <main className={`resume-uploader-main-container ${shouldHideInfoPanel ? "analysis-mode" : ""}`}>
          <div
            className={`resume-uploader-tabs-container ${
              !isInfoDismissed && !shouldHideInfoPanel ? "resume-uploader-tabs-container-hidden" : ""
            } ${shouldHideInfoPanel ? "full-width" : ""}`}
          >
            <Tabs
              items={items}
              size="small"
              activeKey={activeTab}
              onChange={onTabChange}
              style={{ margin: 0, width: "100%" }}
            />

            <div className="resume-uploader-tabs-content-container">
              {getResumeUploaderTabsContent()}
            </div>
          </div>
          {!shouldHideInfoPanel && (
            <div
              className={`resume-uploader-info-container ${
                isInfoDismissed ? "resume-uploader-info-container-dismissed" : ""
              }`}
            >
              <div className="resume-uploader-title-container">
                <Typography.Title
                  level={5}
                  style={{ margin: 0, textAlign: "center" }}
                >
                  Don't forget to add the following details on your resume
                </Typography.Title>
              </div>

              <Divider style={{ margin: "0" }} />

              <div>
                <List
                  itemLayout="horizontal"
                  size="small"
                  bordered={false}
                  dataSource={data}
                  split={false}
                  renderItem={(item, index) => (
                    <List.Item key={index}>
                      <List.Item.Meta
                        avatar={
                          <div className="resume-uploader-info-icon">
                            {item.icon}
                          </div>
                        }
                        title={
                          <Typography.Text type="secondary" style={{fontWeight: 500}}>
                            {item.title}
                          </Typography.Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
              <div className="resume-uploader-info-cta-button-container">
                <Button
                  type="primary"
                  block
                  onClick={handleGotItClick}
                >
                  Proceed
                </Button>
              </div>
            </div>
          )}
        </main>
      </Modal>
    </section>
  );
};

export default ResumeUploader;
